import { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { createPortal } from "react-dom"
import { IoIosArrowDown, IoIosClose } from "react-icons/io"
import { HiOutlineUserGroup } from "react-icons/hi2"
import { LuBed } from "react-icons/lu"
import { FaSearch } from "react-icons/fa"
import tw from "tailwind-styled-components"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { useMutation } from "react-query"
import { toast } from "react-toastify"
import Config from "../Config"
import { Save_User, Save_Property, Select_Property, Save_Partners, Select_Partner } from "../Redux/actions";
import moment from "moment"
import axios from "axios"

const TopRightPartnersDropdown = ({ partners }) => {
    const navigate = useNavigate();
    const user = useSelector((state) => state.UserReducer.user);
    const [isSwitching, setIsSwitching] = useState(false);
    const dispatch = useDispatch()
    const dropdownRef = useRef(null)
    const buttonRef = useRef(null)
    const searchInputRef = useRef(null)
    const [isOpen, setIsOpen] = useState(false)
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, width: 0 })
    const [screenSize, setScreenSize] = useState("desktop")
    const [searchTerm, setSearchTerm] = useState("")
    const storedPartner = useSelector((state) => state.SelectedPartnerReducer.selectPartner);
    //const partners = useSelector((state) => state.PartnersReducer.partners);
    const selectedPartner = storedPartner;
    const Navigate = useNavigate()

    //------- Switch Partner Request -------
    // API Call
    const switchAccount = async ({ clientId, parentId }) => {
        return axios.post(
            `${Config.apiUrl}/client/switch`,
            { clientId, parentId: parentId || 0 },
            {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                    "Content-Type": "application/json",
                },
            }
        );
    };

    // Success Handler
    const onSwitchSuccess = async (res) => {
        setIsSwitching(false);

        setIsOpen(false);
        setSearchTerm("");

        if (!res?.data?.isSuccess) return;

        const token = res.data.token;

        try {
            const propertyRes = await propertyListing(token);
            const properties = propertyRes?.data?.data || [];

            // Aggregate counts
            const totals = properties.reduce(
                (acc, p) => ({
                    tenant: acc.tenant + (p.tenantCount || 0),
                    occupied: acc.occupied + (p.occupiedBedCount || 0),
                    beds: acc.beds + (p.bedCount || 0),
                }),
                { tenant: 0, occupied: 0, beds: 0 }
            );

            const allProperty = {
                id: 0,
                name: "All Properties",
                gId: "KIPINN",
                type: 0,
                tenantPreference: 3,
                tenantCount: totals.tenant,
                occupiedBedCount: totals.occupied,
                bedCount: totals.beds,
            };

            const expireAt = moment()
                .add(Config.sessionExpiredTime, "minutes")
                .valueOf();

            const realData = {
                ...user,       // keep existing user data
                token: res.data.token,
                expireAt,
            };

            // Update redux
            dispatch(Save_User(realData));
            dispatch(Save_Property([allProperty, ...properties]));
            dispatch(Select_Property(allProperty));
            dispatch(Save_Partners(res?.data?.personalAndPartnerProperties));

            // Redirect logic
            navigate("/dashboard");

        } catch (err) {
            toast.error("Something went wrong while loading properties.");
        }
    };

    // Error handler
    const onSwitchError = (err) => {
        setIsSwitching(false);
        toast.error(err?.response?.data?.msg || "An error occurred");
    };

    // Mutation
    const { mutate: postSwitchAccount, isLoading } = useMutation(
        switchAccount,
        { onSuccess: onSwitchSuccess, onError: onSwitchError }
    );


    const propertyListing = async (token) =>
        await axios.get(`${Config.apiUrl}/property/list/web/dropdown`, {
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer" + " " + token,
            },
        });

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth
            if (width < 768) {
                setScreenSize("mobile")
            } else if (width >= 768 && width < 992) {
                setScreenSize("tablet")
            } else {
                setScreenSize("desktop")
            }
        }

        handleResize()
        window.addEventListener("resize", handleResize)
        return () => window.removeEventListener("resize", handleResize)
    }, [])

    const filteredData = partners.filter(partner =>
        partner?.clientName?.toLowerCase().includes(searchTerm.toLowerCase())
    )

    const calculateDropdownPosition = useCallback((rect) => {
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        const dropdownWidth = 320
        const dropdownHeight = 500

        let top = rect.bottom + window.scrollY + 12

        // RIGHT aligned positioning
        let right = viewportWidth - rect.right - window.scrollX

        // Prevent left overflow on smaller screens
        if (viewportWidth - right < dropdownWidth + 16) {
            right = 16
        }

        // Vertical overflow handling
        if (top + dropdownHeight > viewportHeight + window.scrollY) {
            if (rect.top + window.scrollY > dropdownHeight) {
                top = rect.top + window.scrollY - dropdownHeight - 16
            } else {
                top = viewportHeight + window.scrollY - dropdownHeight - 20
            }
        }

        return { top, right }
    }, [])

    useEffect(() => {
        if (isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect()
            const position = calculateDropdownPosition(rect)
            setDropdownPosition(position)

            setTimeout(() => {
                if (searchInputRef.current) {
                    searchInputRef.current.focus()
                }
            }, 100)
        }
    }, [isOpen, screenSize, calculateDropdownPosition])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target)
            ) {
                setIsOpen(false)
                setSearchTerm("")
            }
        }

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside)
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [isOpen])

    const handleSwitchPartner = useCallback((data) => {
        //localStorage.setItem("selectedPartner", JSON.stringify(data));
        dispatch(Select_Partner(data));
        setIsSwitching(true);
        postSwitchAccount({
            clientId: data.clientId,
            parentId: data.parentId || data.clientId
        });

    }, [dispatch])

    const handleSearchChange = useCallback((e) => {
        setSearchTerm(e.target.value)
    }, [])

    const handleClearSearch = useCallback(() => {
        setSearchTerm("")
        setTimeout(() => {
            if (searchInputRef.current) {
                searchInputRef.current.focus()
            }
        }, 0)
    }, [])

    const PortalDropdownMenu = useMemo(() => {
        return (
            <DropdownMenuPortal
                ref={dropdownRef}
                style={{
                    top: dropdownPosition.top,
                    right: dropdownPosition.right,
                }}
            >
                {isSwitching && (
                    <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-[999999]">
                        <p className="text-gray-700 text-sm mt-2">Switching…</p>
                    </div>
                )}
                {/* Search Bar */}
                <SearchContainer>
                    <SearchIcon>
                        <FaSearch
                            size={13}
                            style={{
                                color: '#98A2B3',
                            }}
                        />
                    </SearchIcon>
                    <SearchInput
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search partners..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        key="search-input"
                    />
                    {searchTerm && (
                        <ClearButton onClick={handleClearSearch}>
                            <IoIosClose size={20} className="text-gray-400 hover:text-gray-600" />
                        </ClearButton>
                    )}
                </SearchContainer>

                {/* Properties List */}
                <PropertiesList>
                    {filteredData.length > 0 ? (
                        filteredData.map((data) => {
                            return (
                                <Card key={data?.id} onClick={() => handleSwitchPartner(data)}>
                                    <Header>
                                        <Name>{data?.clientName}</Name>
                                    </Header>
                                    <PropertyDetails>
                                        <StatItem title="Active Tenants">
                                            <HiOutlineUserGroup size={16} className="text-primary-color" />
                                            <PropertyStatus className="font-medium">{data?.tenantCount || 0}</PropertyStatus>
                                        </StatItem>
                                        <StatItem title="Total Beds/Total Properties">
                                            <LuBed size={16} className="text-primary-color" />
                                            <PropertyStatus>
                                                {data?.totalBeds || 0}/{data?.propertyCount || 0}
                                            </PropertyStatus>
                                        </StatItem>
                                    </PropertyDetails>
                                </Card>
                            )
                        })
                    ) : (
                        <NoResults>
                            No data found matching "{searchTerm}"
                        </NoResults>
                    )}
                </PropertiesList>
            </DropdownMenuPortal>
        )
    }, [dropdownPosition, searchTerm, filteredData, handleSwitchPartner, handleSearchChange, handleClearSearch, isSwitching])

    return (
        <DropdownContainer>
            <DropdownButton
                ref={buttonRef}
                onClick={() => {
                    if (partners === false) {
                        Navigate(`/properties`)
                    } else {
                        setIsOpen(!isOpen)
                    }
                }}
            >
                {/* <ProfileInitial>
                    <span className="text-sm font-semibold tracking-wide">
                        {selectedPartner?.clientName?.slice(0, 2).toUpperCase()}
                    </span>
                </ProfileInitial> */}
                <div className="flex flex-col items-start min-w-0 px-2">
                    <div className="flex items-center gap-1.5">
                        <Title>{selectedPartner?.clientName || "Select Partner"}</Title>
                        <IoIosArrowDown
                            size={16}
                            className={`
                            transition-all duration-200
                            text-[#7B8794]
                            ${isOpen ? 'rotate-180' : ''}
                            `}
                        />
                    </div>
                    <StatText className="flex items-center">
                        <LuBed size={15} className="text-[#0078BD] mr-1" />
                        {selectedPartner?.totalOccupiedBeds || 0}/{selectedPartner?.totalBeds || 0}
                    </StatText>
                </div>
            </DropdownButton>

            {isOpen && Array.isArray(partners) && createPortal(PortalDropdownMenu, document.body)}
        </DropdownContainer>
    )
}

// Styled Components
const DropdownContainer = tw.div`relative custom-scrollbar`
const DropdownButton1 = tw.button`flex items-center space-x-2 pr-4 py-2 text-gray-600 text-base md:text-lg`
const DropdownButton = tw.button`
  flex items-center gap-3
  px-3 py-2
  rounded-2xl
  bg-[#F8FAFC]
  hover:bg-[#F4F8FB]
  transition-all duration-200
`
const DropdownMenuPortal1 = tw.div`
    fixed bg-white border border-gray-200 rounded-lg shadow-2xl 
    max-h-[500px] custom-scrollbar overflow-y-auto
    w-72 min-w-[288px]
    z-[99999]
`
const DropdownMenuPortal = tw.div`
  fixed
  bg-white/95
  backdrop-blur-xl
  rounded-[14px]
  shadow-[0_20px_60px_rgba(15,23,42,0.12)]
  border border-[#EEF2F6]
  max-h-[500px]
  overflow-y-auto
  w-80
  z-[99999]
`

const SearchContainer = tw.div`relative p-3 border-b border-gray-200`
const SearchIcon1 = tw.div`absolute left-6 top-1/2 transform -translate-y-1/2`
const SearchIcon = tw.div`
  absolute
  pl-3
  left-4
  top-1/2
  z-10
  -translate-y-1/2
  pointer-events-none
  flex items-center justify-center
`
const SearchInput1 = tw.input`
    w-full pl-10 pr-8 py-2 bg-[#F4F8FB] border-none rounded-lg 
    focus:outline-none focus:ring-2 focus:ring-primary-color focus:border-transparent
    text-sm
`
const SearchInput = tw.input`
    w-full
    pl-11
    pr-8
    py-3
    rounded-2xl
    bg-[#F4F8FB]
    focus:outline-none
    focus:ring-2
    focus:ring-[#0078BD]/10
    text-sm
    text-[#1F2937]
    placeholder:text-[#98A2B3]
`

const ClearButton = tw.button`
    absolute right-6 top-1/2 transform -translate-y-1/2 
    text-gray-400 hover:text-gray-600 text-lg font-bold
    w-5 h-5 flex items-center justify-center
`
const PropertiesList = tw.div`max-h-[400px] overflow-y-auto`
const Card = tw.div`p-3 hover:bg-[#F8FBFD] cursor-pointer border-b border-gray-100 last:border-b-0 w-full`
const Header = tw.div`flex justify-between items-center mb-1`
const Name = tw.div`font-medium text-gray-900`
const PropertyDetails = tw.div`flex items-center justify-between text-sm text-gray-600 mt-2`
const PropertyType = tw.div`text-xs font-medium rounded-md px-2 py-1`
const StatItem = tw.div`flex items-center gap-1`
const PropertyStatus = tw.div`text-sm text-gray-600`
//const Title = tw.h1`text-base font-bold flex items-center text-black w-[120px] truncate`
const Title = tw.h1`
  text-[15px]
  font-semibold
  text-[#1F2937]
  max-w-[150px]
  truncate
`
//const StatText = tw.div`text-sm text-gray-500 text-left`
const StatText = tw.div`
  flex items-center gap-1
  text-[13px]
  text-[#7B8794]
  font-medium
`
const ProfileInitial1 = tw.div`
    w-11 h-11 flex items-center justify-center bg-primary-color text-white rounded-2xl text-lg font-bold
`
const ProfileInitial = tw.div`
  w-11 h-11
  flex items-center justify-center
  rounded-2xl
  bg-[#EAF1F6]
  text-[#5B6B79]
  shadow-[0_1px_2px_rgba(15,23,42,0.03)]
  transition-all duration-200
`

const NoResults = tw.div`p-4 text-center text-gray-500 text-sm`

export default TopRightPartnersDropdown